import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useCadastrarUsuario,
  useEditarUsuario,
  useUsuarios,
} from '../../shared/api/queries.ts';
import type { Usuario } from '../../shared/api/tipos.ts';
import { copiar } from '../../shared/copiar.ts';
import { formatarDataHora } from '../../shared/format.ts';

const TAMANHO_MINIMO_SENHA = 8;

/** Sem I, l, O, 0 e 1: a senha vai ser lida em voz alta ou copiada à mão. */
const ALFABETO = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/**
 * Senha sorteada no navegador, para o administrador não inventar uma.
 *
 * Quem escolhe senha de outra pessoa escolhe a mesma todas as vezes, e ela
 * costuma ser o nome do escritório com um número. Aqui ela nasce aleatória e
 * aparece em texto porque precisa ser passada ao contador — é a única tela em
 * que uma senha é legível, e ela existe por um instante só: depois de salva,
 * nem esta tela nem a API têm como mostrá-la de novo.
 */
function sortearSenha(): string {
  const sorteio = new Uint32Array(14);
  crypto.getRandomValues(sorteio);
  return [...sorteio].map((valor) => ALFABETO[valor % ALFABETO.length]).join('') + '#7';
}

/** Sugestão de login a partir da razão social: `CONTABILIDADE ALFA` → `alfa`. */
function sugerirLogin(razaoSocial: string): string {
  const palavras = razaoSocial
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((palavra) => palavra !== '' && !DESCARTAVEIS.has(palavra));

  return (palavras[0] ?? 'contador').slice(0, 20);
}

const DESCARTAVEIS = new Set([
  'contabilidade',
  'contabil',
  'escritorio',
  'assessoria',
  'servicos',
  'ltda',
  'me',
  'eireli',
  'sa',
  's',
  'a',
  'de',
  'do',
  'da',
  'e',
]);

/**
 * Os acessos do contador desta contabilidade.
 *
 * Mora dentro da tela da contabilidade, e não na de usuários, porque a
 * pergunta é sobre a carteira: "quem do escritório tal entra aqui?". O vínculo
 * sai daqui pronto — criar o acesso nesta tela é o que torna impossível um
 * contador existir sem contabilidade, que é o que define o que ele vê.
 *
 * O que ele enxerga depois é só os clientes vinculados logo acima: consultar,
 * baixar `.zip`, planilha e XML. Não cadastra, não manda e-mail e não vê
 * instalação nem as outras contabilidades.
 */
export function AcessosDoContador({
  contabilidadeId,
  razaoSocial,
}: {
  contabilidadeId: string;
  razaoSocial: string;
}) {
  const acessos = useUsuarios(contabilidadeId);
  const cadastrar = useCadastrarUsuario();
  const editar = useEditarUsuario();

  const [criando, setCriando] = useState(false);
  const [campos, setCampos] = useState({ nome: '', usuario: '', senha: '' });
  const [trocandoSenha, setTrocandoSenha] = useState<Usuario | null>(null);
  const [senhaNova, setSenhaNova] = useState('');

  const salvando = cadastrar.isPending || editar.isPending;
  const erro = cadastrar.error ?? editar.error;

  const lista = acessos.data?.itens ?? [];

  // Mesmo formato do domínio, só para a resposta ser imediata.
  const usuarioOk = /^[a-z0-9][a-z0-9._-]{2,39}$/.test(campos.usuario);
  const podeCriar =
    campos.nome.trim().length >= 3 &&
    usuarioOk &&
    campos.senha.length >= TAMANHO_MINIMO_SENHA &&
    !salvando;

  function abrirCriacao() {
    setCampos({
      nome: `Contador · ${razaoSocial}`.slice(0, 120),
      usuario: sugerirLogin(razaoSocial),
      senha: sortearSenha(),
    });
    setTrocandoSenha(null);
    cadastrar.reset();
    editar.reset();
    setCriando(true);
  }

  async function criar() {
    if (!podeCriar) return;
    try {
      await cadastrar.mutateAsync({
        nome: campos.nome.trim(),
        usuario: campos.usuario,
        senha: campos.senha,
        papel: 'contador',
        contabilidadeId,
      });
      setCriando(false);
      setCampos({ nome: '', usuario: '', senha: '' });
    } catch {
      // A mensagem já está em `erro`; o formulário fica aberto para corrigir.
    }
  }

  function abrirTrocaDeSenha(acesso: Usuario) {
    setCriando(false);
    cadastrar.reset();
    editar.reset();
    setSenhaNova(sortearSenha());
    setTrocandoSenha(acesso);
  }

  async function salvarSenha() {
    if (trocandoSenha === null || senhaNova.length < TAMANHO_MINIMO_SENHA) return;
    try {
      await editar.mutateAsync({
        id: trocandoSenha.id,
        nome: trocandoSenha.nome,
        usuario: trocandoSenha.usuario,
        papel: trocandoSenha.papel,
        ativo: trocandoSenha.ativo,
        senha: senhaNova,
      });
      setTrocandoSenha(null);
      setSenhaNova('');
    } catch {
      // A mensagem já está em `erro`.
    }
  }

  function alternarSituacao(acesso: Usuario) {
    cadastrar.reset();
    editar.reset();
    editar.mutate({
      id: acesso.id,
      nome: acesso.nome,
      usuario: acesso.usuario,
      papel: acesso.papel,
      ativo: !acesso.ativo,
    });
  }

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Acesso do contador
        </Typography>
        <Button
          type="button"
          size="small"
          startIcon={<AddIcon />}
          onClick={abrirCriacao}
          disabled={criando}
        >
          Novo acesso
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Com este login, o contador entra no painel e vê apenas os clientes vinculados acima:
        consulta as notas, baixa o <code>.zip</code>, a planilha e o XML. Não cadastra nada,
        não manda e-mail e não vê as outras contabilidades.
      </Typography>

      {erro !== null && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {mensagemDoErro(erro)}
        </Alert>
      )}

      {acessos.isPending ? (
        <Skeleton variant="rounded" height={48} />
      ) : lista.length === 0 && !criando ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum acesso criado para esta contabilidade.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {lista.map((acesso) => (
            <Stack
              key={acesso.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {acesso.usuario}
                  </Typography>
                  {!acesso.ativo && <Chip size="small" label="Inativo" variant="outlined" />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {acesso.nome} ·{' '}
                  {acesso.ultimoAcessoEm === null
                    ? 'nunca entrou'
                    : `último acesso ${formatarDataHora(acesso.ultimoAcessoEm)}`}
                </Typography>
              </Box>

              <Button
                type="button"
                size="small"
                startIcon={<KeyOutlinedIcon />}
                onClick={() => abrirTrocaDeSenha(acesso)}
                disabled={salvando}
              >
                Trocar senha
              </Button>

              <Button
                type="button"
                size="small"
                color={acesso.ativo ? 'error' : 'success'}
                onClick={() => alternarSituacao(acesso)}
                disabled={salvando}
              >
                {acesso.ativo ? 'Inativar' : 'Ativar'}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      {trocandoSenha !== null && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <CampoSenha
            rotulo={`Nova senha de ${trocandoSenha.usuario}`}
            valor={senhaNova}
            aoMudar={setSenhaNova}
            ajuda="Trocar a senha encerra as sessões abertas dele. Copie antes de salvar: depois
              daqui, nem o painel tem como mostrá-la."
          />
          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={salvarSenha}
              disabled={senhaNova.length < TAMANHO_MINIMO_SENHA || salvando}
              loading={editar.isPending}
            >
              Salvar senha
            </Button>
            <Button type="button" size="small" onClick={() => setTrocandoSenha(null)}>
              Cancelar
            </Button>
          </Stack>
        </Stack>
      )}

      {criando && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Nome"
            size="small"
            value={campos.nome}
            onChange={(evento) => setCampos((atual) => ({ ...atual, nome: evento.target.value }))}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />

          <TextField
            label="Usuário"
            size="small"
            value={campos.usuario}
            onChange={(evento) =>
              setCampos((atual) => ({
                // Minúsculas na digitação, como o domínio normalizaria.
                ...atual,
                usuario: evento.target.value.toLowerCase().replace(/\s/g, ''),
              }))
            }
            fullWidth
            autoComplete="off"
            error={campos.usuario !== '' && !usuarioOk}
            helperText={
              campos.usuario !== '' && !usuarioOk
                ? 'De 3 a 40 caracteres: letras sem acento, números, ponto, hífen e sublinhado.'
                : 'É o que o contador digita para entrar.'
            }
            slotProps={{
              htmlInput: {
                maxLength: 40,
                autoCapitalize: 'none',
                autoCorrect: 'off',
                spellCheck: false,
              },
            }}
          />

          <CampoSenha
            rotulo="Senha"
            valor={campos.senha}
            aoMudar={(senha) => setCampos((atual) => ({ ...atual, senha }))}
            ajuda="Copie e passe ao contador. Depois de salvar, nem o painel tem como
              mostrá-la de novo."
          />

          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={criar}
              disabled={!podeCriar}
              loading={cadastrar.isPending}
            >
              Criar acesso
            </Button>
            <Button type="button" size="small" onClick={() => setCriando(false)}>
              Cancelar
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

/**
 * Senha à mostra, com copiar e sortear de novo.
 *
 * Em texto de propósito: aqui ela não é segredo de quem digita, é um valor que
 * precisa sair da tela e chegar ao contador. Esconder atrás de asteriscos
 * levaria o administrador a inventar uma senha curta só para conseguir ditá-la.
 */
function CampoSenha({
  rotulo,
  valor,
  aoMudar,
  ajuda,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  ajuda: string;
}) {
  return (
    <TextField
      label={rotulo}
      size="small"
      value={valor}
      onChange={(evento) => aoMudar(evento.target.value)}
      fullWidth
      autoComplete="off"
      error={valor !== '' && valor.length < TAMANHO_MINIMO_SENHA}
      helperText={
        valor !== '' && valor.length < TAMANHO_MINIMO_SENHA
          ? `Ao menos ${TAMANHO_MINIMO_SENHA} caracteres.`
          : ajuda
      }
      slotProps={{
        htmlInput: { maxLength: 200, spellCheck: false, style: { fontFamily: 'monospace' } },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Copiar">
                <IconButton
                  size="small"
                  aria-label="Copiar senha"
                  onClick={() => void copiar(valor)}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button type="button" size="small" onClick={() => aoMudar(sortearSenha())}>
                Sortear
              </Button>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    return erro.codigo === 'DOMINIO_CONFLITO' && erro.detalhes?.usuario !== undefined
      ? 'Este nome de usuário já está em uso.'
      : erro.message;
  }
  return erro instanceof Error ? erro.message : 'Não foi possível salvar o acesso.';
}
